import { useEffect, useMemo, useState } from "react";
import { api, ApiRequestError } from "../api/client";
import { useCurrentCase } from "../hooks/useCurrentCase";

type InquiryStatus =
  | "new"
  | "reviewing"
  | "replied"
  | "meeting_scheduled"
  | "accepted"
  | "closed"
  | "not_target";
type InquirySource = "form" | "chatbot";
type QuestionType = "text" | "textarea" | "radio" | "checkbox" | "select" | "date" | "file" | "address" | "phone" | "email";
type RequiredLevel = "required" | "recommended" | "optional";

interface InquiryAnswer {
  question_id?: string | null;
  question_label: string;
  answer: string;
  required_level?: RequiredLevel;
  answered_by?: InquirySource;
}

interface Inquiry {
  id: string;
  source: InquirySource;
  business_type: string;
  summary: string;
  status: InquiryStatus;
  urgency: "high" | "medium" | "low" | "unknown";
  contact_name: string;
  email: string | null;
  phone: string | null;
  preferred_contact_method: string | null;
  ai_summary: string | null;
  ai_next_questions: string[];
  created_at: string;
  updated_at: string;
  answers: InquiryAnswer[];
}

interface InquiryQuestion {
  id: string;
  label: string;
  help_text: string | null;
  placeholder: string | null;
  type: QuestionType;
  required_level: RequiredLevel;
  options: string[];
  sort_order: number;
  used_by_form: boolean;
  used_by_chatbot: boolean;
  show_if: unknown | null;
}

interface InquiryTemplate {
  id: string;
  business_type: string;
  title: string;
  description: string | null;
  is_active: boolean;
  questions: InquiryQuestion[];
}

interface ContactFormSettings {
  services: InquiryTemplate[];
}

const STATUS_LABELS: Record<InquiryStatus, string> = {
  new: "未対応",
  reviewing: "確認中",
  replied: "返信済み",
  meeting_scheduled: "面談予定",
  accepted: "受任済み",
  closed: "完了",
  not_target: "対象外",
};

const STATUS_OPTIONS = Object.entries(STATUS_LABELS) as [InquiryStatus, string][];

const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: "text", label: "短文入力" },
  { value: "textarea", label: "長文入力" },
  { value: "radio", label: "単一選択" },
  { value: "checkbox", label: "複数選択" },
  { value: "select", label: "選択リスト" },
  { value: "date", label: "日付" },
  { value: "file", label: "ファイル添付" },
  { value: "address", label: "住所" },
  { value: "phone", label: "電話番号" },
  { value: "email", label: "メール" },
];

const REQUIRED_LABELS: Record<RequiredLevel, string> = {
  required: "必須",
  recommended: "推奨",
  optional: "任意",
};

const REQUIRED_DESCRIPTIONS: Record<RequiredLevel, string> = {
  required: "送信前に必ず確認したい項目です。",
  recommended: "あると返信や面談がスムーズになります。",
  optional: "分かる範囲で任意に聞く項目です。",
};

const QUESTION_TYPE_LABELS = QUESTION_TYPES.reduce<Record<QuestionType, string>>((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {} as Record<QuestionType, string>);

const jstDate = (s: string) =>
  new Date(s).toLocaleString("ja-JP", { dateStyle: "medium", timeStyle: "short" });

function normalizeSettings(value: unknown): ContactFormSettings {
  const raw = value && typeof value === "object" ? value as Record<string, unknown> : {};
  const services = Array.isArray(raw.services) ? raw.services : [];
  return {
    services: services.map((item, serviceIndex) => {
      const row = item && typeof item === "object" ? item as Record<string, unknown> : {};
      const businessType = String(row.business_type ?? row.name ?? "その他").trim();
      const questions = Array.isArray(row.questions) ? row.questions : [];
      return {
        id: String(row.id ?? `local-${serviceIndex}`),
        business_type: businessType,
        title: String(row.title ?? `${businessType}についてご相談を伺います`),
        description: row.description == null ? null : String(row.description),
        is_active: row.is_active !== false && row.enabled !== false,
        questions: questions.map((q, index) => {
          const qRow = q && typeof q === "object" ? q as Record<string, unknown> : {};
          const options = Array.isArray(qRow.options) ? qRow.options.map((opt) => String(opt ?? "")).filter(Boolean) : [];
          return {
            id: String(qRow.id ?? `local-${serviceIndex}-${index}`),
            label: String(qRow.label ?? q ?? ""),
            help_text: qRow.help_text == null ? null : String(qRow.help_text),
            placeholder: qRow.placeholder == null ? null : String(qRow.placeholder),
            type: (QUESTION_TYPES.some((t) => t.value === qRow.type) ? qRow.type : "textarea") as QuestionType,
            required_level: (["required", "recommended", "optional"].includes(String(qRow.required_level)) ? qRow.required_level : "optional") as RequiredLevel,
            options,
            sort_order: Number(qRow.sort_order ?? index + 1),
            used_by_form: qRow.used_by_form !== false,
            used_by_chatbot: qRow.used_by_chatbot !== false,
            show_if: qRow.show_if ?? null,
          };
        }),
      };
    }).filter((service) => service.business_type),
  };
}

function splitOptions(value: string): string[] {
  return value.split(/\n|,/).map((item) => item.trim()).filter(Boolean);
}

function optionsText(options: string[]): string {
  return options.join("\n");
}

export function ContactInboxPage() {
  const { caseRow, loading } = useCurrentCase();
  const [contacts, setContacts] = useState<Inquiry[]>([]);
  const [settings, setSettings] = useState<ContactFormSettings>({ services: [] });
  const [activeView, setActiveView] = useState<"inbox" | "settings" | "chatTest">("inbox");
  const [activeServiceIndex, setActiveServiceIndex] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | InquiryStatus>("all");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = async (caseId: string) => {
    const [res, settingRes] = await Promise.all([
      api.get<{ contacts: Inquiry[] }>(`/api/cases/${caseId}/contacts`),
      api.get<{ settings: ContactFormSettings }>(`/api/cases/${caseId}/contact-form-settings`),
    ]);
    setSettings(normalizeSettings(settingRes.settings));
    setContacts(res.contacts);
    setSelectedId((current) => current ?? res.contacts[0]?.id ?? null);
  };

  useEffect(() => {
    if (!caseRow) return;
    setFetching(true);
    reload(caseRow.id)
      .catch((err) =>
        setError(err instanceof ApiRequestError ? err.message : "お問い合わせの読み込みに失敗しました"),
      )
      .finally(() => setFetching(false));
  }, [caseRow?.id]);

  const filtered = useMemo(
    () => contacts.filter((item) => filter === "all" || item.status === filter),
    [contacts, filter],
  );
  const selected = filtered.find((item) => item.id === selectedId) ?? filtered[0] ?? null;
  const stats = {
    all: contacts.length,
    new: contacts.filter((item) => item.status === "new").length,
    reviewing: contacts.filter((item) => item.status === "reviewing").length,
    meeting: contacts.filter((item) => item.status === "meeting_scheduled").length,
    accepted: contacts.filter((item) => item.status === "accepted").length,
  };

  const updateStatus = async (item: Inquiry, status: InquiryStatus) => {
    if (!caseRow) return;
    setBusyId(item.id);
    setError(null);
    try {
      await api.patch(`/api/cases/${caseRow.id}/contacts/${item.id}`, { status });
      await reload(caseRow.id);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "ステータス更新に失敗しました");
    } finally {
      setBusyId(null);
    }
  };

  const updateService = (index: number, patch: Partial<InquiryTemplate>) => {
    setSettings((current) => ({
      services: current.services.map((service, i) => i === index ? { ...service, ...patch } : service),
    }));
  };

  const updateQuestion = (serviceIndex: number, questionIndex: number, patch: Partial<InquiryQuestion>) => {
    setSettings((current) => ({
      services: current.services.map((service, i) =>
        i === serviceIndex
          ? {
              ...service,
              questions: service.questions.map((q, qi) => qi === questionIndex ? { ...q, ...patch } : q),
            }
          : service,
      ),
    }));
  };

  const addService = () => {
    setSettings((current) => {
      const next = {
        services: [
          ...current.services,
          {
            id: `local-${Date.now()}`,
            business_type: "新しい業務",
            title: "新しい業務についてご相談を伺います",
            description: null,
            is_active: true,
            questions: [newQuestion("相談内容", "textarea", "required")],
          },
        ],
      };
      setActiveServiceIndex(next.services.length - 1);
      return next;
    });
  };

  const addQuestion = (serviceIndex: number, level: RequiredLevel) => {
    setSettings((current) => ({
      services: current.services.map((service, i) =>
        i === serviceIndex
          ? { ...service, questions: [...service.questions, newQuestion("", "textarea", level)] }
          : service,
      ),
    }));
  };

  const removeQuestion = (serviceIndex: number, questionIndex: number) => {
    setSettings((current) => ({
      services: current.services.map((service, i) =>
        i === serviceIndex
          ? { ...service, questions: service.questions.filter((_, qi) => qi !== questionIndex) }
          : service,
      ),
    }));
  };

  const saveSettings = async () => {
    if (!caseRow) return;
    setSavingSettings(true);
    setNotice(null);
    setError(null);
    try {
      const res = await api.patch<{ settings: ContactFormSettings }>(
        `/api/cases/${caseRow.id}/contact-form-settings`,
        settings,
      );
      setSettings(normalizeSettings(res.settings));
      setNotice("お問い合わせフォーム設計を保存しました。フォームとAIチャットの両方に反映されます。");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "フォーム設計の保存に失敗しました");
    } finally {
      setSavingSettings(false);
    }
  };

  if (loading || fetching) return <div className="page-center">読み込み中...</div>;
  if (!caseRow) return <div className="page-center">公開済みサイトが見つかりません。</div>;

  return (
    <section className="lp-page lp-contact-inbox">
      <div className="lp-page-head">
        <div>
          <span className="lp-section-kicker">CONTACT</span>
          <h1>お問い合わせ</h1>
          <p>フォームとAIチャットから届いた相談を、一次ヒアリング済みの状態で確認できます。</p>
        </div>
        <button className="btn btn-primary" onClick={() => setActiveView("chatTest")}>
          チャットbotをテスト
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <div className="lp-view-tabs">
        <button className={activeView === "inbox" ? "active" : ""} onClick={() => setActiveView("inbox")}>受信一覧</button>
        <button className={activeView === "settings" ? "active" : ""} onClick={() => setActiveView("settings")}>フォーム設計</button>
        <button className={activeView === "chatTest" ? "active" : ""} onClick={() => setActiveView("chatTest")}>チャットテスト</button>
      </div>

      {activeView === "settings" ? (
        <ContactFormDesigner
          settings={settings}
          activeServiceIndex={activeServiceIndex}
          setActiveServiceIndex={setActiveServiceIndex}
          updateService={updateService}
          updateQuestion={updateQuestion}
          addService={addService}
          addQuestion={addQuestion}
          removeQuestion={removeQuestion}
          saveSettings={saveSettings}
          saving={savingSettings}
        />
      ) : activeView === "chatTest" ? (
        <ChatbotTester caseId={caseRow.id} templates={settings.services} />
      ) : (
        <ContactInbox
          contacts={contacts}
          filtered={filtered}
          selected={selected}
          filter={filter}
          setFilter={setFilter}
          setSelectedId={setSelectedId}
          stats={stats}
          updateStatus={updateStatus}
          busyId={busyId}
        />
      )}
    </section>
  );
}

function newQuestion(label: string, type: QuestionType, level: RequiredLevel): InquiryQuestion {
  return {
    id: `local-q-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    label,
    help_text: null,
    placeholder: null,
    type,
    required_level: level,
    options: [],
    sort_order: 0,
    used_by_form: true,
    used_by_chatbot: true,
    show_if: null,
  };
}

function ContactInbox({
  contacts,
  filtered,
  selected,
  filter,
  setFilter,
  setSelectedId,
  stats,
  updateStatus,
  busyId,
}: {
  contacts: Inquiry[];
  filtered: Inquiry[];
  selected: Inquiry | null;
  filter: "all" | InquiryStatus;
  setFilter: (value: "all" | InquiryStatus) => void;
  setSelectedId: (id: string) => void;
  stats: { all: number; new: number; reviewing: number; meeting: number; accepted: number };
  updateStatus: (item: Inquiry, status: InquiryStatus) => void;
  busyId: string | null;
}) {
  if (contacts.length === 0) {
    return (
      <div className="lp-card lp-empty-state">
        <h2>まだお問い合わせは届いていません</h2>
        <p>公開サイトの「お問い合わせ」ボタンから、フォームまたはAIチャットで相談が届きます。</p>
      </div>
    );
  }
  return (
    <>
      <div className="lp-metric-row lp-contact-summary">
        <button className={filter === "all" ? "active" : ""} onClick={() => setFilter("all")}>すべて <strong>{stats.all}</strong></button>
        <button className={filter === "new" ? "active" : ""} onClick={() => setFilter("new")}>新着 <strong>{stats.new}</strong></button>
        <button className={filter === "reviewing" ? "active" : ""} onClick={() => setFilter("reviewing")}>確認中 <strong>{stats.reviewing}</strong></button>
        <button className={filter === "meeting_scheduled" ? "active" : ""} onClick={() => setFilter("meeting_scheduled")}>面談予定 <strong>{stats.meeting}</strong></button>
        <button className={filter === "accepted" ? "active" : ""} onClick={() => setFilter("accepted")}>受任済み <strong>{stats.accepted}</strong></button>
      </div>
      <div className="lp-contact-grid">
        <div className="lp-card lp-contact-list">
          {filtered.map((item) => (
            <button key={item.id} className={selected?.id === item.id ? "active" : ""} onClick={() => setSelectedId(item.id)}>
              <span className={`lp-contact-status ${item.status}`}>{STATUS_LABELS[item.status]}</span>
              <strong>{item.contact_name || "お名前未入力"}</strong>
              <small>{jstDate(item.created_at)}</small>
              <p>{item.business_type} / {item.source === "chatbot" ? "AIチャット" : "フォーム"} / {item.summary}</p>
            </button>
          ))}
        </div>
        <aside className="lp-card lp-contact-detail">
          {selected ? (
            <>
              <div className="lp-contact-detail-head">
                <span className={`lp-contact-status ${selected.status}`}>{STATUS_LABELS[selected.status]}</span>
                <small>{jstDate(selected.created_at)}</small>
              </div>
              <h2>{selected.contact_name || "お名前未入力"}</h2>
              <p className="lp-ai-summary">{selected.ai_summary || selected.summary}</p>
              <dl>
                <dt>相談種別</dt><dd>{selected.business_type}</dd>
                <dt>経路</dt><dd>{selected.source === "chatbot" ? "AIチャット" : "フォーム"}</dd>
                <dt>メール</dt><dd>{selected.email || "未入力"}</dd>
                <dt>電話番号</dt><dd>{selected.phone || "未入力"}</dd>
                <dt>希望連絡方法</dt><dd>{selected.preferred_contact_method || "指定なし"}</dd>
              </dl>
              <div className="lp-answer-table">
                <h3>回答内容</h3>
                {selected.answers.map((answer) => (
                  <div key={`${answer.question_label}-${answer.answer}`}>
                    <span>{answer.question_label}</span>
                    <strong>{answer.answer}</strong>
                  </div>
                ))}
              </div>
              {selected.ai_next_questions.length > 0 && (
                <div className="lp-next-question-box">
                  <h3>次に確認するとよいこと</h3>
                  <ul>{selected.ai_next_questions.map((q) => <li key={q}>{q}</li>)}</ul>
                </div>
              )}
              <div className="lp-contact-actions">
                {selected.email && <a className="btn btn-primary" href={`mailto:${selected.email}`}>返信する</a>}
                {selected.phone && <a className="btn btn-ghost" href={`tel:${selected.phone}`}>電話する</a>}
                <select value={selected.status} onChange={(e) => updateStatus(selected, e.target.value as InquiryStatus)} disabled={busyId === selected.id}>
                  {STATUS_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                </select>
              </div>
            </>
          ) : <p className="muted">表示するお問い合わせがありません。</p>}
        </aside>
      </div>
    </>
  );
}

function ContactFormDesigner({
  settings,
  activeServiceIndex,
  setActiveServiceIndex,
  updateService,
  updateQuestion,
  addService,
  addQuestion,
  removeQuestion,
  saveSettings,
  saving,
}: {
  settings: ContactFormSettings;
  activeServiceIndex: number;
  setActiveServiceIndex: (index: number) => void;
  updateService: (index: number, patch: Partial<InquiryTemplate>) => void;
  updateQuestion: (serviceIndex: number, questionIndex: number, patch: Partial<InquiryQuestion>) => void;
  addService: () => void;
  addQuestion: (serviceIndex: number, level: RequiredLevel) => void;
  removeQuestion: (serviceIndex: number, questionIndex: number) => void;
  saveSettings: () => void;
  saving: boolean;
}) {
  const active = settings.services[activeServiceIndex] ?? settings.services[0];
  const activeIndex = active ? settings.services.indexOf(active) : -1;
  const activeQuestions = active?.questions ?? [];
  const activeFormQuestions = activeQuestions.filter((q) => q.used_by_form);
  const activeChatQuestions = activeQuestions.filter((q) => q.used_by_chatbot);
  return (
    <div className="lp-contact-form-builder">
      <div className="lp-contact-form-hero">
        <div>
          <span className="lp-section-kicker">FORM BUILDER</span>
          <h2>問い合わせ前に聞くことを整える</h2>
          <p>訪問者が迷わず送信でき、先生側では初回返信に必要な情報が揃うように設計します。</p>
        </div>
        <div className="lp-form-builder-stats" aria-label="フォーム設計の概要">
          <span><strong>{settings.services.filter((s) => s.is_active).length}</strong>表示中の業務</span>
          <span><strong>{activeQuestions.length}</strong>この業務の質問</span>
          <span><strong>{activeFormQuestions.length}</strong>フォームで表示</span>
          <span><strong>{activeChatQuestions.length}</strong>チャットで使用</span>
        </div>
      </div>

      <div className="lp-contact-form-grid">
        <aside className="lp-card lp-contact-service-list">
          <div className="lp-card-headline">
            <div>
              <h2>業務</h2>
              <p>相談の入口です</p>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={addService}>追加</button>
          </div>
          <div className="lp-service-list-scroll">
            {settings.services.map((service, index) => {
              const requiredCount = service.questions.filter((q) => q.required_level === "required").length;
              return (
                <button key={`${service.id}-${index}`} className={index === activeServiceIndex ? "active" : ""} onClick={() => setActiveServiceIndex(index)}>
                  <span className={`lp-service-dot ${service.is_active ? "on" : "off"}`} />
                  <strong>{service.business_type}</strong>
                  <small>{service.questions.length}問 / 必須{requiredCount}問</small>
                  <em>{service.is_active ? "公開フォームに表示" : "非表示"}</em>
                </button>
              );
            })}
          </div>
        </aside>

        <main className="lp-card lp-contact-form-editor">
          {active ? (
            <>
              <div className="lp-editor-toolbar">
                <div>
                  <h2>{active.business_type}</h2>
                  <p>質問文、入力方法、フォーム/チャットで使うかをここで調整します。</p>
                </div>
                <div className="lp-editor-toolbar-actions">
                  <label className="lp-toggle-line">
                    <input type="checkbox" checked={active.is_active} onChange={(e) => updateService(activeIndex, { is_active: e.target.checked })} />
                    表示する
                  </label>
                  <button className="btn btn-primary" onClick={saveSettings} disabled={saving}>{saving ? "保存中..." : "保存"}</button>
                </div>
              </div>

              <div className="lp-service-basics">
                <label className="field">
                  <span className="field-label">業務名</span>
                  <input value={active.business_type} onChange={(e) => updateService(activeIndex, { business_type: e.target.value, title: `${e.target.value}についてご相談を伺います` })} />
                </label>
                <label className="field">
                  <span className="field-label">公開フォームの説明</span>
                  <input value={active.description ?? ""} onChange={(e) => updateService(activeIndex, { description: e.target.value })} placeholder="例）分かる範囲で入力してください。初回返信時に追加で確認します。" />
                </label>
              </div>

              <div className="lp-question-lanes">
                {(["required", "recommended", "optional"] as RequiredLevel[]).map((level) => {
                  const questions = active.questions
                    .map((question, index) => ({ question, index }))
                    .filter((item) => item.question.required_level === level);
                  return (
                    <section className={`lp-question-lane ${level}`} key={level}>
                      <div className="lp-question-lane-head">
                        <div>
                          <span>{REQUIRED_LABELS[level]}</span>
                          <p>{REQUIRED_DESCRIPTIONS[level]}</p>
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={() => addQuestion(activeIndex, level)}>質問を追加</button>
                      </div>
                      <div className="lp-question-list">
                        {questions.length > 0 ? questions.map(({ question, index }) => (
                          <QuestionEditor
                            key={question.id}
                            question={question}
                            number={index + 1}
                            onChange={(patch) => updateQuestion(activeIndex, index, patch)}
                            onRemove={() => removeQuestion(activeIndex, index)}
                          />
                        )) : (
                          <div className="lp-question-empty">この区分の質問はまだありません。</div>
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>
            </>
          ) : <p className="muted">業務を追加してください。</p>}
        </main>

        <aside className="lp-card lp-contact-form-preview">
          <div className="lp-preview-head">
            <div>
              <h2>公開時の見え方</h2>
              <p>訪問者にはこの順番で表示されます。</p>
            </div>
            <span>{active?.is_active ? "表示中" : "非表示"}</span>
          </div>
          <div className="lp-preview-panel">
            <span className="lp-form-step">1</span>
            <strong>何に関するお問い合わせですか？</strong>
            <div className="lp-preview-choice-list">
              {settings.services.filter((service) => service.is_active).map((service) => (
                <button key={service.id} className={service === active ? "active" : ""}>{service.business_type}</button>
              ))}
            </div>
          </div>
          {active && (
            <div className="lp-preview-panel">
              <span className="lp-form-step">2</span>
              <strong>{active.business_type}について確認します</strong>
              <ol className="lp-preview-question-flow">
                {active.questions.filter((q) => q.used_by_form).slice(0, 10).map((q) => (
                  <li key={q.id}>
                    <span>{REQUIRED_LABELS[q.required_level]}</span>
                    <p>{q.label || "質問文未入力"}</p>
                    <small>{QUESTION_TYPE_LABELS[q.type]}</small>
                  </li>
                ))}
              </ol>
              {active.questions.filter((q) => q.used_by_form).length === 0 && (
                <p className="muted">フォームで使う質問がありません。</p>
              )}
            </div>
          )}
          {active && (
            <div className="lp-preview-panel">
              <span className="lp-form-step">AI</span>
              <strong>チャットbotが確認できること</strong>
              <ul className="lp-preview-chat-list">
                {active.questions.filter((q) => q.used_by_chatbot).slice(0, 6).map((q) => <li key={q.id}>{q.label || "質問文未入力"}</li>)}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function QuestionEditor({
  question,
  number,
  onChange,
  onRemove,
}: {
  question: InquiryQuestion;
  number: number;
  onChange: (patch: Partial<InquiryQuestion>) => void;
  onRemove: () => void;
}) {
  const needsOptions = question.type === "radio" || question.type === "checkbox" || question.type === "select";
  return (
    <article className="lp-question-card">
      <div className="lp-question-card-top">
        <span className="lp-question-number">Q{number}</span>
        <label className="field lp-question-title-field">
          <span className="field-label">質問文</span>
          <input value={question.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="例）いつまでに手続きを進めたいですか？" />
        </label>
        <button className="btn btn-ghost btn-sm" onClick={onRemove}>削除</button>
      </div>

      <div className="lp-question-card-grid">
        <label className="field">
          <span className="field-label">入力方法</span>
          <select value={question.type} onChange={(e) => onChange({ type: e.target.value as QuestionType })}>
            {QUESTION_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">重要度</span>
          <select value={question.required_level} onChange={(e) => onChange({ required_level: e.target.value as RequiredLevel })}>
            {(["required", "recommended", "optional"] as RequiredLevel[]).map((level) => (
              <option key={level} value={level}>{REQUIRED_LABELS[level]}</option>
            ))}
          </select>
        </label>
        <div className="lp-channel-switches" aria-label="質問を使う場所">
          <label className={question.used_by_form ? "active" : ""}><input type="checkbox" checked={question.used_by_form} onChange={(e) => onChange({ used_by_form: e.target.checked })} /> フォーム</label>
          <label className={question.used_by_chatbot ? "active" : ""}><input type="checkbox" checked={question.used_by_chatbot} onChange={(e) => onChange({ used_by_chatbot: e.target.checked })} /> AIチャット</label>
        </div>
      </div>

      <label className="field">
        <span className="field-label">補足説明</span>
        <input value={question.help_text ?? ""} onChange={(e) => onChange({ help_text: e.target.value })} placeholder="例）分からない場合は「未定」と入力してもらいます。" />
      </label>

      <label className="field">
        <span className="field-label">入力欄のプレースホルダー</span>
        <input value={question.placeholder ?? ""} onChange={(e) => onChange({ placeholder: e.target.value })} placeholder="例）内装工事、電気工事、管工事など" />
      </label>

      {needsOptions && (
        <label className="field">
          <span className="field-label">選択肢</span>
          <textarea value={optionsText(question.options)} onChange={(e) => onChange({ options: splitOptions(e.target.value) })} placeholder={"できるだけ早く\n1か月以内\n3か月以内\n未定"} />
        </label>
      )}
    </article>
  );
}

function ChatbotTester({ caseId, templates }: { caseId: string; templates: InquiryTemplate[] }) {
  const [messages, setMessages] = useState<{ role: "bot" | "user"; text: string }[]>([
    { role: "bot", text: "公開サイトのチャットbotの動作をここで確認できます。試しに相談内容を入力してください。" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((current) => [...current, { role: "user", text }]);
    setBusy(true);
    try {
      const res = await api.post<{ reply: string }>(`/api/cases/${caseId}/chatbot-test`, { message: text });
      setMessages((current) => [...current, { role: "bot", text: res.reply }]);
    } catch (err) {
      setMessages((current) => [...current, { role: "bot", text: err instanceof ApiRequestError ? err.message : "応答に失敗しました" }]);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="lp-chat-test-grid">
      <main className="lp-card lp-chat-test-panel">
        <h2>チャットbotテスト</h2>
        <div className="lp-chat-test-log">
          {messages.map((message, index) => <div key={index} className={`lp-chat-test-msg ${message.role}`}>{message.text}</div>)}
        </div>
        <div className="lp-chat-test-input">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void send(); }} placeholder="例）建設業許可を取りたいのですが、自分で取れるかわかりません" />
          <button className="btn btn-primary" onClick={send} disabled={busy}>{busy ? "確認中..." : "送信"}</button>
        </div>
      </main>
      <aside className="lp-card lp-chat-test-side">
        <h2>チャットが参照する質問</h2>
        {templates.filter((template) => template.is_active).map((template) => (
          <div key={template.id}>
            <strong>{template.business_type}</strong>
            <ul>{template.questions.filter((q) => q.used_by_chatbot).slice(0, 6).map((q) => <li key={q.id}>{q.label}</li>)}</ul>
          </div>
        ))}
      </aside>
    </div>
  );
}
